/**
 * The Subskrypcja tab: how many seats the plan carries, when it renews, where it is managed, and —
 * since 21 September 2026 — where it is bought.
 *
 * **It sells now.** Requirement R13 makes this the one web surface for subscriptions: on the free
 * plan it offers the levels and leads to the payment page (`PlanPicker`), on a web subscription it
 * leads to the provider's portal, and on a store subscription it stays exactly as read-only as it
 * was, because the channel that sold a subscription is the only one that manages it (R4).
 *
 * Every decision is `model/subscriptionCard`'s and `model/webOffers`': which sentence, which count,
 * whether there is a store to open or a portal to open instead, whether a bar is drawn at all.
 * This file lays them out.
 *
 * ── The bar is decoration, and the legend is not ────────────────────────────────────────────
 * The segments are `aria-hidden`: every number they encode is already written in words above and
 * beside them, and a row of cells read out one by one is noise. What a listener gets instead is the
 * figure, the caption and `seatsA11y` — the whole bar as one sentence.
 */

import { useTranslation } from 'react-i18next';

import { FormBanner } from '~/shared/ui/FormBanner';
import ghost from '~/shared/ui/ghostButton.module.css';
import inlineText from '~/shared/ui/inlineTextButton.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { SeatSegment } from '~/entities/client';

import type { AccountSubscriptionApi } from '../lib/useSubscriptionCard';
import type { WebCheckoutApi } from '../lib/useWebCheckout';
import { ExternalIcon, MailIcon } from './AccountIcons';
import { PlanPicker } from './PlanPicker';
import screen from './AccountScreen.module.css';
import styles from './SubscriptionTab.module.css';

interface SubscriptionTabProps {
  readonly subscription: AccountSubscriptionApi;
  readonly checkout: WebCheckoutApi;
}

/** A seat's treatment, written once: the bar's cells and the legend's marks wear the same three. */
const SEAT_CLASS: Readonly<Record<SeatSegment, string>> = {
  active: String(styles.seatActive),
  pending: String(styles.seatPending),
  free: '',
};

export function SubscriptionTab({ subscription, checkout }: SubscriptionTabProps) {
  const { t } = useTranslation();
  const { card, state, waiting, skeleton, reveal } = subscription;
  // The one subscriber line the reserved row may hold, by priority (see the row's comment).
  const subscriberLine = card?.parallelBilling ?? card?.pendingChange ?? null;

  return (
    <div className={screen.inner}>
      <div className={screen.lede}>
        {/* No lede. The owner dropped the two-line explanation on 18 September 2026 — what a plan
          * decides is already written in the seat figure under it. */}
        <h2 className={screen.h2}>{t('web:account.subscription.heading')}</h2>
      </div>

      {/* The failure is content too: it waits for the bars' minimum life like the figures do, so
        * the banner does not stand over a block still drawing its skeleton. */}
      {!waiting && state === 'error' && (
        <div className={screen.block}>
          <FormBanner message={t('web:account.subscription.loadFailed')} />
          <button
            type="button"
            className={`${String(ghost.ghost)} ${String(screen.accountButton)} ${String(screen.canvasGhost)} ${String(styles.retry)}`}
            onClick={subscription.onRetry}
          >
            {t('common.retry')}
          </button>
        </div>
      )}

      {/* The seats block keeps its height in all three states — the figure's line, the bar's line
        * and the legend's are sized in the stylesheet — so the answer landing changes ink only. */}
      {/* `aria-busy` and the hidden sentence follow the WAIT, like the bars inside the block and
        * the banner above it — a block still standing its skeleton has not finished changing. */}
      <div className={screen.block} aria-busy={waiting}>
        <h3 className={screen.blockTitle}>{t('web:account.subscription.seatsTitle')}</h3>
        {waiting && (
          <span className={hidden.srOnly}>{t('web:account.subscription.waiting')}</span>
        )}

        {/* ── ONE reserved row, where the canon draws the line that stands in it ──────────────
          * Frame D of `docs/design/subskrypcja-2026-09-21/index.html` puts the queued-change line
          * inside „Miejsca", above the figure. Three sentences can stand there — the wait after a
          * payment, the queued end-of-period change, and the double-billing warning (R3) — and they
          * are the same kind of line in the same place, so they share one box.
          *
          * It is drawn in EVERY state of this tab, and that is the whole reserve. Which of them
          * applies is the entitlement's answer, and measured on a cold entry with 900ms of latency
          * injected, a row conditioned on that answer moved the figure, the bar and the legend by
          * 45px the moment it landed — 89px with two lines in it. A row conditioned the other way,
          * present while the read is open and withdrawn when the answer says „free", moves them the
          * same 45px upwards instead. The empty row on a free trainer's tab is what buys 0px for
          * everybody, and it is empty rather than blank: no ink, no band, just the room the line
          * would take. Departure recorded in `docs/design/README.md`.
          *
          * 45px is one line's box, and exactly one line is ever drawn in it, by priority: the
          * checkout notice, then the double billing, then the queued change. */}
        <div className={styles.pendingRow}>
          {checkout.notice !== null && (
            <p className={styles.pending}>
              {checkout.notice}
              {/* The minute's cap is where the wait stops, not where the trainer's options do:
                * a subscription that took longer than a minute to ingest is the ordinary case
                * this sentence is for, and „zajrzyj tu za chwilę" with nothing to press means
                * reloading the page. One press re-arms the same wait, with the same cap. */}
              {checkout.onRetryActivation !== null && (
                <button
                  type="button"
                  /* Inside the sentence, in the sentence's own size: a 34px button box here
                    * would outgrow the row's 45px reserve, which is the thing the row is for. */
                  className={`${String(inlineText.inlineTextButton)} ${String(styles.noticeRetry)}`}
                  onClick={checkout.onRetryActivation}
                >
                  {t('web:account.subscription.activatingRetry')}
                </button>
              )}
            </p>
          )}

          {/* Both of these belong to a subscription that EXISTS, so neither can be drawn on the
            * picker face — there is no band for a free trainer, only the room one would take.
            * R3 first: „you are paying twice" outranks a queued change, and the row holds ONE line —
            * two at once grew it by 44px on the probe, which is the movement the row exists to
            * prevent. The queued change returns the moment the second billing is gone. Both are said
            * in words, never in colour alone. */}
          {checkout.face === 'rows' && checkout.notice === null && subscriberLine !== null && (
            <p className={styles.pending}>{subscriberLine}</p>
          )}
        </div>

        <div className={styles.figureRow}>
          {skeleton ? (
            <span className={`${String(skel.bar)} ${String(styles.figureBar)}`} />
          ) : (
            <b className={`${String(styles.figure)} ${String(reveal)}`}>{card?.seatFigure ?? 0}</b>
          )}
          {/* Rendered only once there IS a caption. An empty span standing where the caption will
            * go is not a reserve — it sits hard against the skeleton bar and is then PUSHED right by
            * the number that replaces it, which measured 113px of single-frame movement on a cold
            * entry with 900ms of latency injected. The row's own height is the reserve; the two
            * pieces of the answer arrive together, in place. */}
          {card !== null && <span className={styles.figureCaption}>{card.seatCaption}</span>}
        </div>

        <div className={styles.barRow}>
          {skeleton ? (
            <span className={`${String(skel.bar)} ${String(styles.barSkeleton)}`} />
          ) : (
            card !== null &&
            card.segments.length > 0 && (
              <span className={`${String(styles.bar)} ${String(reveal)}`} aria-hidden="true">
                {card.segments.map((segment, index) => (
                  <i key={index} className={`${String(styles.seat)} ${SEAT_CLASS[segment]}`} />
                ))}
              </span>
            )
          )}
        </div>

        {/* The bar as one sentence — what the cells above would otherwise only say in colour. */}
        {card !== null && <span className={hidden.srOnly}>{card.seatsA11y}</span>}

        {/* The legend's LINE is drawn in every state, empty while the read is open. Rendered only
          * once the entries exist, it added 26px to this block the moment the answer landed and
          * pushed the three rows under it down by 19px — measured on a cold entry with 900ms of
          * latency injected. An unlimited plan keeps the empty line for the same reason the bar's
          * row above it is kept: the block's height is the reserve, and it cannot depend on which
          * plan answered. */}
        <p className={styles.legend}>
          {card !== null &&
            card.legend.map((entry) => (
              <span key={entry.kind} className={styles.legendEntry}>
                <i
                  className={`${String(styles.legendMark)} ${SEAT_CLASS[entry.kind]}`}
                  aria-hidden="true"
                />
                {entry.label}
              </span>
            ))}
        </p>
      </div>

      {/* ── ONE block under the seats, and both faces stand in it ──────────────────────────────
        * The picker stands directly under the seats block, where the canon puts it: the seats are
        * today's truth and the moment the upsell makes sense, and the cards answer it.
        *
        * WHICH face is the entitlement's answer, and until it lands the answer is neither — which
        * is what `face === 'pending'` says. It draws the PICKER's reserve, because the picker is
        * the taller of the two. Reading `isFree` off a read that has not answered says „false",
        * and that drew a subscriber's „Odnowienie / Kupiona w" rows at every free trainer on a
        * cold load, then unmounted them when the picker arrived. */}
      {/* Since 23 September 2026 the cards stand for EVERY trainer, subscriber included: without
        * them a paying trainer saw one „Otwórz portal" row and nothing saying the level could be
        * changed at all. What a card's press does is the channel's (`checkout.route`). Only a
        * failed entitlement read withdraws them — the banner above already says so, and a wait
        * under a reported failure is a second, silent one. */}
      {(checkout.face === 'pending' || checkout.route !== null) && <PlanPicker checkout={checkout} />}

      {/* The read-only rows, for a subscription that EXISTS. The canon's frame A draws none of them
        * beside the picker, and it is right to: „Odnowienie —" and „Kupiona w —" are two dashes
        * where the block above already says the plan is free and the block below says what it would
        * take to change that. */}
      {checkout.face === 'rows' && (
        <div className={`${String(screen.block)} ${String(screen.rows)}`}>
          <div className={screen.row}>
            <span className={screen.rowText}>
              <b className={screen.rowTitle}>
                {card?.renewalTitle ?? t('web:account.subscription.renewal')}
              </b>
              {/* A running trial's „cancel before … and nothing is charged" (R19). The note's LINE is
                * drawn on every subscriber's row, empty where there is no note: whether there is one
                * is the trial read's answer, and a line arriving with it grew this row by 4px and
                * pushed every row under it, measured on a cold entry with 700ms of latency. */}
              {card?.renewalNote != null ? (
                <span className={`${String(screen.rowNote)} ${String(reveal)}`}>{card.renewalNote}</span>
              ) : (
                <span className={screen.rowNote} aria-hidden="true">
                  {'\u00a0'}
                </span>
              )}
            </span>
            {/* The value arrives WHOLE or not at all. A placeholder standing in its place is
              * right-aligned text of a different width, so the answer landing slides its left edge —
              * 113px, measured. An em dash is the right thing to draw once the read is in and there
              * is genuinely no renewal; it is the wrong thing to draw while nobody knows yet. */}
            {card !== null && (
              <span className={`${String(screen.rowValue)} ${String(reveal)}`}>
                {card.renewal ?? '—'}
              </span>
            )}
          </div>

          <div className={screen.row}>
            <span className={screen.rowText}>
              <b className={screen.rowTitle}>{t('web:account.subscription.boughtIn')}</b>
            </span>
            {card !== null && (
              <span className={`${String(screen.rowValue)} ${String(reveal)}`}>
                {card.boughtIn ?? '—'}
              </span>
            )}
          </div>

          {/* The block's last two rows depend on the CHANNEL — a web subscription has a portal row
            * and an invoice row where a store subscription has one link and a comped one has
            * nothing — so a block built only from what has answered grows by 122px the moment the
            * answer says „Web", measured on a cold entry with 900ms of latency injected. The boxes
            * are drawn empty while nobody knows, exactly as the seats block reserves its three
            * lines, and the answer fills them in place. */}
          {card === null && (
            <>
              <div className={screen.row} />
              <div className={screen.row} />
            </>
          )}

          {/* Only when a storefront genuinely manages this plan. A comped subscription has nowhere to
            * send anybody — Apple's subscription list does not carry it, and a link into it reads as
            * broken. A WEB subscription has somewhere, and it is a button rather than a link: the
            * portal's address is minted per press and is valid for minutes. */}
          {card?.manage === 'store' && card.store !== null && (
            <a
              className={`${String(screen.row)} ${String(styles.manage)}`}
              href={card.store.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('web:account.app.help.opensExternally', {
                label: t('web:account.subscription.manage'),
              })}
            >
              <span className={screen.rowText}>
                <b className={screen.rowTitle}>{t('web:account.subscription.manage')}</b>
                <span className={screen.rowNote}>
                  {t('web:account.subscription.manageNote', { store: card.store.name })}
                </span>
              </span>
              <ExternalIcon className={screen.rowIcon} />
            </a>
          )}

          {/* The web's own two rows, for a subscription bought here. The portal does every change
            * R8 allows — level, period, card, cancelling — and the receipts live in it, which is why
            * this tab keeps no payment history of its own. */}
          {card?.manage === 'portal' && (
            <>
              <div className={screen.row}>
                <span className={screen.rowText}>
                  <b className={screen.rowTitle}>{t('web:account.subscription.manage')}</b>
                  <span className={screen.rowNote}>{t('web:account.subscription.portalNote')}</span>
                </span>
                <button
                  type="button"
                  className={`${String(ghost.ghost)} ${String(screen.accountButton)} ${String(screen.canvasGhost)}`}
                  aria-disabled={checkout.portalBusy}
                  onClick={() => {
                    if (checkout.portalBusy) return;
                    checkout.onOpenPortal();
                  }}
                >
                  <ExternalIcon className={screen.rowIcon} />
                  {t('web:account.subscription.portalAction')}
                </button>
              </div>

              {/* By hand at launch (R15), so the row is an address and a sentence rather than a form.
                * The subject is prefilled and nothing else is: an account's own address in a `mailto`
                * query string is personal data written into a URL. */}
              <div className={screen.row}>
                <span className={screen.rowText}>
                  <b className={screen.rowTitle}>{t('web:account.subscription.invoiceTitle')}</b>
                  <span className={screen.rowNote}>{t('web:account.subscription.invoiceNote')}</span>
                </span>
                <span className={`${String(screen.rowValue)} ${String(styles.mailValue)}`}>
                  {/* The canon's `mail` glyph, beside the address rather than instead of it: frame D
                    * draws it and the first build dropped it. `aria-hidden` like every other glyph
                    * here — the row's title already says „Faktura VAT". */}
                  <MailIcon className={screen.rowIcon} />
                  <a
                    className={styles.mail}
                    href={`mailto:${t('web:account.subscription.invoiceAddress')}?subject=${encodeURIComponent(
                      t('web:account.subscription.invoiceSubject'),
                    )}`}
                  >
                    {t('web:account.subscription.invoiceAddress')}
                  </a>
                </span>
              </div>
            </>
          )}

          {checkout.portalError !== null && (
            <div className={screen.row}>
              <FormBanner message={checkout.portalError} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
